import type { ReactNode } from 'react';

import type { PopperPlacementType } from '@mui/material';

import { useGetTypesenseDocumentById } from '@/api/typesense/document';
import { EntityCard } from '@/components/ui/EntityCard';
import { HoverCard } from '@/components/ui/HoverCard';
import type { TypesenseAPIKeyName } from '@/hooks/filters/useTypesenseAPIKey';
import { useHoverCard } from '@/hooks/ui';
import { buildHoverCardFields } from '@/lib/resource-config';
import type { HoverCardConfig } from '@/lib/resource-config';

interface EntityHoverCardProps {
  children: ReactNode;
  entityId: string | number | null | undefined;
  config: HoverCardConfig;
  apiKeyName?: TypesenseAPIKeyName;
  placement?: PopperPlacementType;
}

const ENTITY_FETCH_PARAMS: Record<
  'employees' | 'customers',
  { collection: string; idField: string; apiKeyName: TypesenseAPIKeyName }
> = {
  customers: {
    collection: 'customers',
    idField: 'SourceId',
    apiKeyName: 'customers',
  },
  employees: {
    collection: 'employees',
    idField: 'SourceId',
    apiKeyName: 'employees',
  },
};

export const EntityHoverCard = ({
  children,
  entityId,
  config,
  apiKeyName,
  placement = 'bottom-start',
}: EntityHoverCardProps) => {
  const hover = useHoverCard({ delayMs: config.delayMs });

  const fetchParams = config.type ? ENTITY_FETCH_PARAMS[config.type] : null;

  const { data, isLoading, isError } = useGetTypesenseDocumentById({
    collection: fetchParams?.collection ?? null,
    idField: fetchParams?.idField ?? null,
    apiKeyName: apiKeyName ?? fetchParams?.apiKeyName ?? 'employees',
    entityId: entityId != null ? String(entityId) : null,
    enabled: !!hover.anchorEl,
  });

  return (
    <>
      <span
        onMouseEnter={(e) => hover.openCard(e.currentTarget)}
        onMouseLeave={hover.closeCard}
      >
        {children}
      </span>

      <HoverCard
        anchorEl={hover.anchorEl}
        onClose={hover.closeCard}
        onKeepOpen={hover.cancelClose}
        placement={placement}
      >
        <EntityCard
          type={config.type}
          id={entityId != null ? entityId : undefined}
          loading={isLoading}
          error={isError}
          fields={buildHoverCardFields(config, data)}
        />
      </HoverCard>
    </>
  );
};
